import { Mic, MicOff, Search, X, RefreshCw } from "lucide-react";
import { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../Services/Store/Store";
import {
  setConnecting,
  setConnected,
  setMuted,
  setError as setAgentError,
  setSessionData,
  updateTokenUsage,
} from "../../Services/Store/Slice/agentSlice";
import {
  useSearchSpaceSessionMutation,
  useLazySearchSpaceQuery,
  useExchangeSDPMutation,
  useTrackTokensMutation,
  useGetTokenStatusQuery,
} from "../../Services/Store/Slice/agentApi";
import { useNavigate } from "react-router";

interface ApiError {
  data?: { error?: string };
  message?: string;
}

interface SearchSpaceAgentProps {
  autoStart?: boolean;
  onDisconnect?: () => void;
}

interface SearchFilters {
  name?: string;
  address?: string;
  minRate?: number;
  maxRate?: number;
  minCapacity?: number;
  maxCapacity?: number;
  tags?: string;
  features?: string;
}

interface Space {
  id: string;
  tagLine?: string;
  name: string;
  address: string;
  ratePerHour: number;
  capacity: number;
  media?: Array<{ url: string }>;
  tags?: Array<{ text: string }>;
  features?: Array<{ text: string }>;
  days?: string[];
}

export const SearchSpaceAgent: React.FC<SearchSpaceAgentProps> = ({
  autoStart = false,
  onDisconnect,
}) => {
  const router = useNavigate();
  const dispatch = useDispatch();
  const agentState = useSelector((state: RootState) => state.agent);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioElementRef = useRef<HTMLAudioElement>(new Audio());
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const hasAutoStartedRef = useRef(false);
  const hasLoadedDefaultSpacesRef = useRef(false);

  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});
  const [searchResults, setSearchResults] = useState<Space[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const [searchSpaceSession] = useSearchSpaceSessionMutation();
  const [triggerSearchSpace] = useLazySearchSpaceQuery();
  const [exchangeSDP] = useExchangeSDPMutation();
  const [trackTokens] = useTrackTokensMutation();
  useGetTokenStatusQuery(undefined);

  const pcConfig = {
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
  };

  const performSearch = async (filters: SearchFilters) => {
    try {
      setIsSearching(true);
      const result = await triggerSearchSpace(filters).unwrap();
      console.log("Search result:", result);
      setSearchResults(result.data || []);

      return {
        success: true,
        message: `Found ${result.data?.length || 0} spaces`,
        totalResults: result.pagination?.totalItems || 0,
        spaces:
          result.data?.slice(0, 3).map((space: Space) => ({
            id: space.id,
            tagLine: space.tagLine,
            name: space.name,
            address: space.address,
            ratePerHour: space.ratePerHour,
            capacity: space.capacity,
            tags: space.tags?.map((t) => t.text).join(", "),
            features: space.features?.map((f) => f.text).join(", "),
          })) || [],
        appliedFilters: filters,
        activeFilterCount: Object.keys(filters).length,
      };
    } catch (error: any) {
      console.error("Search error:", error);
      return {
        error:
          error.message || error.data?.message || "Failed to search spaces",
        appliedFilters: filters,
      };
    } finally {
      setIsSearching(false);
    }
  };

  // useEffect(() => {
  //   if (!hasLoadedDefaultSpacesRef.current) {
  //     hasLoadedDefaultSpacesRef.current = true;
  //     console.log("Loading default spaces...");
  //     performSearch({});
  //   }
  // }, []);

  const getFilterDisplayName = (key: string): string => {
    const displayNames: Record<string, string> = {
      minRate: "Min Price",
      maxRate: "Max Price",
      minCapacity: "Min Capacity",
      maxCapacity: "Max Capacity",
      name: "Name",
      address: "Location",
      tags: "Vibe",
      features: "Features",
    };
    return displayNames[key] || key;
  };

  const handleFunctionCall = useCallback(
    async (callId: string, functionName: string, args: string) => {
      try {
        const parsedArgs = JSON.parse(args);
        let result;

        if (functionName === "search_spaces") {
          setCurrentFilters((prevFilters) => {
            const updatedFilters = { ...prevFilters };

            Object.entries(parsedArgs).forEach(([key, value]) => {
              if (value === null || value === "" || value === undefined) {
                delete updatedFilters[key as keyof SearchFilters];
              } else {
                updatedFilters[key as keyof SearchFilters] = value as any;
              }
            });

            performSearch(updatedFilters).then((searchResult) => {
              const enhancedResult = {
                ...searchResult,
                filterAction: "incremental_add",
                addedFilters: Object.keys(parsedArgs),
                totalActiveFilters: Object.keys(updatedFilters).length,
                currentFilters: updatedFilters,
              };

              if (dataChannelRef.current?.readyState === "open") {
                dataChannelRef.current.send(
                  JSON.stringify({
                    type: "conversation.item.create",
                    item: {
                      type: "function_call_output",
                      call_id: callId,
                      output: JSON.stringify(enhancedResult),
                    },
                  })
                );

                dataChannelRef.current.send(
                  JSON.stringify({
                    type: "response.create",
                    response: {
                      modalities: ["audio", "text"],
                    },
                  })
                );
              }
            });

            return updatedFilters;
          });

          return;
        } else if (functionName === "remove_filter") {
          setCurrentFilters((prevFilters) => {
            const updatedFilters = { ...prevFilters };
            const filtersToRemove = parsedArgs.filters || [];

            const removedFilters: string[] = [];
            filtersToRemove.forEach((filterKey: string) => {
              if (updatedFilters[filterKey as keyof SearchFilters]) {
                delete updatedFilters[filterKey as keyof SearchFilters];
                removedFilters.push(filterKey);
              }
            });

            performSearch(updatedFilters).then((searchResult) => {
              const enhancedResult = {
                ...searchResult,
                filterAction: "removed",
                removedFilters,
                remainingFilters: Object.keys(updatedFilters),
              };

              if (dataChannelRef.current?.readyState === "open") {
                dataChannelRef.current.send(
                  JSON.stringify({
                    type: "conversation.item.create",
                    item: {
                      type: "function_call_output",
                      call_id: callId,
                      output: JSON.stringify(enhancedResult),
                    },
                  })
                );

                dataChannelRef.current.send(
                  JSON.stringify({
                    type: "response.create",
                    response: {
                      modalities: ["audio", "text"],
                    },
                  })
                );
              }
            });

            return updatedFilters;
          });

          return;
        } else if (functionName === "clear_all_filters") {
          console.log("Clearing all filters");

          setCurrentFilters((prevFilters) => {
            const previousFilters = { ...prevFilters };

            performSearch({}).then((searchResult) => {
              const enhancedResult = {
                ...searchResult,
                filterAction: "cleared",
                clearedFilters: Object.keys(previousFilters),
              };

              if (dataChannelRef.current?.readyState === "open") {
                dataChannelRef.current.send(
                  JSON.stringify({
                    type: "conversation.item.create",
                    item: {
                      type: "function_call_output",
                      call_id: callId,
                      output: JSON.stringify(enhancedResult),
                    },
                  })
                );

                dataChannelRef.current.send(
                  JSON.stringify({
                    type: "response.create",
                    response: {
                      modalities: ["audio", "text"],
                    },
                  })
                );
              }
            });

            return {};
          });

          return;
        } else if (functionName === "get_current_filters") {
          const activeFilters = Object.entries(currentFilters)
            .filter(
              ([_, value]) =>
                value !== null && value !== undefined && value !== ""
            )
            .map(([key, value]) => ({
              filter: key,
              value: value,
              displayName: getFilterDisplayName(key),
            }));

          result = {
            success: true,
            activeFilters,
            filterCount: activeFilters.length,
            rawFilters: currentFilters,
          };

          if (dataChannelRef.current?.readyState === "open") {
            dataChannelRef.current.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify(result),
                },
              })
            );

            dataChannelRef.current.send(
              JSON.stringify({
                type: "response.create",
                response: {
                  modalities: ["audio", "text"],
                },
              })
            );
          }
        } else {
          result = { error: `Unknown function: ${functionName}` };

          if (dataChannelRef.current?.readyState === "open") {
            dataChannelRef.current.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify(result),
                },
              })
            );

            dataChannelRef.current.send(
              JSON.stringify({
                type: "response.create",
                response: {
                  modalities: ["audio", "text"],
                },
              })
            );
          }
        }
      } catch (err) {
        console.error("Function call error:", err);
        setIsSearching(false);

        if (dataChannelRef.current?.readyState === "open") {
          dataChannelRef.current.send(
            JSON.stringify({
              type: "conversation.item.create",
              item: {
                type: "function_call_output",
                call_id: callId,
                output: JSON.stringify({
                  error: "Function execution failed",
                  details: err instanceof Error ? err.message : "Unknown error",
                }),
              },
            })
          );

          dataChannelRef.current.send(
            JSON.stringify({
              type: "response.create",
              response: {
                modalities: ["audio", "text"],
              },
            })
          );
        }
      }
    },
    [performSearch, getFilterDisplayName]
  );

  const handleTokenUsage = useCallback(
    async (tokensUsed: number) => {
      try {
        await trackTokens({ tokensUsed }).unwrap();
        dispatch(updateTokenUsage(tokensUsed));

        if (agentState.tokenStatus) {
          const newRemaining = agentState.tokenStatus.remaining - tokensUsed;
          if (newRemaining <= 0) {
            handleDisconnect();
            dispatch(setAgentError("Token limit reached. Session ended."));
          }
        }
      } catch (err) {
        console.error("Token tracking error:", err);
      }
    },
    [trackTokens, dispatch, agentState.tokenStatus]
  );

  const stopMediaStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
        console.log(`Stopped ${track.kind} track`);
      });
      mediaStreamRef.current = null;
    }
  }, []);

  const setupWebRTC = useCallback(
    async (clientSecret: string) => {
      try {
        const pc = new RTCPeerConnection(pcConfig);
        peerConnectionRef.current = pc;

        if (!audioElementRef.current) {
          audioElementRef.current = new Audio();
        }
        audioElementRef.current.autoplay = true;

        pc.ontrack = (event) => {
          if (audioElementRef.current) {
            audioElementRef.current.srcObject = event.streams[0];
            audioElementRef.current.play().catch((err) => {
              console.error("Error playing audio:", err);
            });
          }
        };

        const dataChannel = pc.createDataChannel("oai-events");
        dataChannelRef.current = dataChannel;

        dataChannel.onopen = () => {
          console.log("Data channel opened - triggering initial greeting");

          dataChannel.send(
            JSON.stringify({
              type: "response.create",
              response: {
                modalities: ["audio", "text"],
              },
            })
          );
        };

        dataChannel.onmessage = async (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log("Received message:", message.type, message);

            if (message.type === "response.function_call_arguments.done") {
              await handleFunctionCall(
                message.call_id,
                message.name,
                message.arguments
              );
            }

            if (
              message.type === "response.audio.delta" ||
              message.type === "response.audio.done" ||
              message.type === "response.audio_transcript.delta" ||
              message.type === "response.audio_transcript.done"
            ) {
              console.log("Audio message received:", message.type);
            }

            if (message.type === "response.done" && message.response?.usage) {
              await handleTokenUsage(message.response.usage.total_tokens || 0);
            }
          } catch (err) {
            console.error("Error processing message:", err);
          }
        };

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 24000,
          },
        });

        mediaStreamRef.current = stream;
        console.log("Got user media stream");

        stream.getTracks().forEach((track) => {
          pc.addTrack(track, stream);
        });

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const answerSdp = await exchangeSDP({
          clientSecret,
          sdp: offer.sdp!,
        }).unwrap();

        await pc.setRemoteDescription({
          type: "answer",
          sdp: answerSdp,
        });

        console.log("WebRTC setup complete");
        return pc;
      } catch (err) {
        console.error("WebRTC setup error:", err);
        stopMediaStream();
        throw err;
      }
    },
    [exchangeSDP, handleFunctionCall, handleTokenUsage, stopMediaStream]
  );

  const handleConnect = useCallback(async () => {
    try {
      dispatch(setConnecting(true));

      const sessionResult = await searchSpaceSession().unwrap();
      dispatch(setSessionData(sessionResult.data));

      await setupWebRTC(sessionResult.data.client_secret.value);

      dispatch(setConnected(true));
      console.log("Successfully connected to search agent");
    } catch (err: unknown) {
      console.error("Connection failed:", err);
      const errorMessage =
        (err as ApiError).data?.error ||
        (err as ApiError).message ||
        "Connection failed";
      dispatch(setAgentError(errorMessage));
      stopMediaStream();
    } finally {
      dispatch(setConnecting(false));
    }
  }, [searchSpaceSession, setupWebRTC, dispatch, stopMediaStream]);

  const handleDisconnect = useCallback(() => {
    console.log("Disconnecting search agent...");

    stopMediaStream();

    if (dataChannelRef.current) {
      try {
        if (dataChannelRef.current.readyState === "open") {
          dataChannelRef.current.close();
        }
      } catch (err) {
        console.error("Error closing data channel:", err);
      }
      dataChannelRef.current = null;
      console.log("Closed data channel");
    }

    if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
      } catch (err) {
        console.error("Error closing peer connection:", err);
      }
      peerConnectionRef.current = null;
      console.log("Closed peer connection");
    }

    if (audioElementRef.current) {
      try {
        audioElementRef.current.pause();
        audioElementRef.current.srcObject = null;
      } catch (err) {
        console.error("Error stopping audio element:", err);
      }
      console.log("Stopped audio element");
    }

    dispatch(setConnected(false));
    dispatch(setSessionData(null));
    dispatch(setMuted(false));

    if (onDisconnect) {
      onDisconnect();
    }
  }, [dispatch, stopMediaStream, onDisconnect]);

  const toggleMute = useCallback(() => {
    if (peerConnectionRef.current && mediaStreamRef.current) {
      const audioTracks = mediaStreamRef.current.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = agentState.isMuted;
      });

      dispatch(setMuted(!agentState.isMuted));
    }
  }, [agentState.isMuted, dispatch]);

  const clearFilters = () => {
    setCurrentFilters({});
    performSearch({});
  };

  const removeFilter = (filterKey: string) => {
    const updatedFilters = { ...currentFilters };
    delete updatedFilters[filterKey as keyof SearchFilters];
    setCurrentFilters(updatedFilters);
    performSearch(updatedFilters);
  };

  useEffect(() => {
    if (
      autoStart &&
      !hasAutoStartedRef.current &&
      !agentState.isConnected &&
      !agentState.isConnecting
    ) {
      hasAutoStartedRef.current = true;
      console.log("Auto-starting agent...");
      handleConnect();
    }
  }, [
    autoStart,
    agentState.isConnected,
    agentState.isConnecting,
    handleConnect,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (agentState.isConnected) {
        event.preventDefault();
        event.returnValue = "You are currently connected to the AI agent.";
        return event.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && agentState.isConnected) {
        handleDisconnect();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (agentState.isConnected) {
        console.log("Component unmounting - disconnecting agent");
        handleDisconnect();
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [agentState.isConnected, handleDisconnect]);

  // useEffect(() => {
  //   if (agentState.error) {
  //     const timer = setTimeout(() => dispatch(setAgentError("")), 5000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [agentState.error, dispatch]);

  const formatExpiryDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleNavigate = async (id: string) => {
    handleDisconnect();
    router(`/space/${id}`);
    console.log("Navigating to space with ID:", id);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex justify-center items-center bg-white backdrop-blur-lg rounded-3xl p-6 shadow-sm border border-gray-400">
          <div className="w-full">
            {agentState.tokenStatus && (
              <div className="mb-6 p-2 bg-white/5 rounded-xl border border-white/10">
                <div className="text-xs text-gray-700 mb-2">Token Usage</div>
                <div className="flex justify-between text-black mb-2">
                  <span className="text-xs">Remaining:</span>
                  <span
                    className={`font-mono text-xs ${
                      agentState.tokenStatus.remaining < 1000
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    {agentState.tokenStatus.remaining.toLocaleString()}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                  <div
                    className={`h-2 rounded-full  transition-all duration-300 ${
                      agentState.tokenStatus.remaining < 1000
                        ? "bg-red-500"
                        : agentState.tokenStatus.remaining < 10000
                        ? "bg-yellow-500"
                        : "bg-green-500"
                    }`}
                    style={{
                      width: `${
                        (agentState.tokenStatus.remaining /
                          agentState.tokenStatus.total) *
                        100
                      }%`,
                    }}
                  />
                </div>
                <div className="text-[10px] text-gray-600">
                  Expires: {formatExpiryDate(agentState.tokenStatus.expiresAt)}
                </div>
              </div>
            )}

            {agentState.error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
                <p className="text-red-500 text-sm">{agentState.error}</p>
              </div>
            )}
            <p className="text-black text-center">
              {agentState.isConnected
                ? ""
                : "Hit the Mic Button below to find you perfect space!"}
            </p>

            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={
                  agentState.isConnected ? handleDisconnect : handleConnect
                }
                disabled={agentState.isConnecting}
                className={`rounded-full p-6 font-semibold text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                  agentState.isConnected
                    ? "bg-red-500 hover:bg-red-600 text-black shadow-lg"
                    : "bg-green-500 hover:bg-green-600 text-black shadow-lg"
                }`}
              >
                {agentState.isConnected ? (
                  <MicOff size={24} />
                ) : (
                  <Mic size={24} />
                )}
              </button>
              {agentState.isConnected && (
                <button
                  onClick={toggleMute}
                  className={`p-3 rounded-full transition-all duration-300 transform hover:scale-105 ${
                    agentState.isMuted
                      ? "bg-orange-500 hover:bg-orange-600 text-black"
                      : "bg-gray-500 hover:bg-gray-600 text-black"
                  }`}
                >
                  <MicOff size={16} />
                </button>
              )}
            </div>
            <div className="mt-6">
              <div
                className={`w-4 h-4 rounded-full mx-auto ${
                  agentState.isConnected
                    ? "bg-green-500 animate-pulse"
                    : agentState.isConnecting
                    ? "bg-yellow-500 animate-pulse"
                    : "bg-gray-500"
                }`}
              />

              <p className="text-black text-sm text-center mt-1">
                {agentState.isConnected
                  ? "Connected - AI can hear you"
                  : agentState.isConnecting
                  ? "Connecting..."
                  : "Disconnected"}
              </p>
              {agentState.isConnected && (
                <p className="text-gray-600 text-xs mt-1 text-center">
                  Microphone is {agentState.isMuted ? "muted" : "active"}
                </p>
              )}
            </div>

            {Object.keys(currentFilters).length > 0 && (
              <div className="">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-black flex items-center gap-2">
                    <RefreshCw size={16} />
                    Active Filters ({Object.keys(currentFilters).length})
                  </h3>
                  <button
                    onClick={clearFilters}
                    className="text-xs bg-red-500/20 hover:bg-red-500/30 px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                  >
                    <X size={12} />
                    Clear All
                  </button>
                </div>
                <div className=" flex gap-1">
                  {Object.entries(currentFilters).map(
                    ([key, value]) =>
                      value && (
                        <div
                          key={key}
                          className=" bg-blue-100 px-3 py-1 gap-1 rounded-lg flex justify-between items-center text-center group"
                        >
                          <div className="flex gap-1">
                            <span className="text-gray-600 text-xs uppercase">
                              {getFilterDisplayName(key)}:
                            </span>{" "}
                            <span className="text-black font-medium text-xs">
                              {String(value)}
                            </span>
                          </div>
                          <button
                            onClick={() => removeFilter(key)}
                            className="bg-red-100 transition-opacity p-1 hover:bg-red-500/20 rounded"
                            title={`Remove ${getFilterDisplayName(key)} filter`}
                          >
                            <X size={14} className="text-red-500" />
                          </button>
                        </div>
                      )
                  )}
                </div>
              </div>
            )}

            {isSearching && (
              <div className="mt-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <RefreshCw size={16} className="animate-spin" />
                  <p className="text-sm">🔍 Searching spaces...</p>
                </div>
              </div>
            )}

            <audio ref={audioElementRef} style={{ display: "none" }} />
          </div>
        </div>

        <div className="bg-white backdrop-blur-lg rounded-3xl p-6 shadow-sm border border-gray-400">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Search Results</h2>
            {searchResults.length > 0 && (
              <span className="text-sm text-gray-600 bg-blue-500/20 px-3 py-1 rounded-full">
                {searchResults.length}{" "}
                {searchResults.length === 1 ? "space" : "spaces"} found
              </span>
            )}
          </div>

          {isSearching && searchResults.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 mx-auto mb-4 text-blue-500 animate-spin" />
              <p className="text-gray-600">Loading spaces...</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">
                Your search results will show here as we find matches
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
              {searchResults.map((space) => (
                <div
                  key={space.id}
                  onClick={() => {
                    handleNavigate(space.id);
                  }}
                  className="bg-white/5 border border-white/10 cursor-pointer rounded-xl p-4 hover:bg-white/10 transition-all hover:shadow-lg"
                >
                  <div className="flex gap-4">
                    {space.media && space.media.length > 0 && (
                      <img
                        src={space.media[0].url}
                        alt={space.name}
                        className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0 text-left">
                      <h3 className="font-bold text-lg mb-1 truncate">
                        {space.tagLine}
                      </h3>
                

                      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600">Rate:</span>
                          <span className="font-semibold text-green-600">
                            ${space.ratePerHour}/hr
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600">Capacity:</span>
                          <span className="font-semibold">
                            {space.capacity} people
                          </span>
                        </div>
                      </div>

                      {space.days && space.days.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {space.days.map((day) => (
                            <span
                              key={day}
                              className="text-xs bg-blue-500/20 px-2 py-1 rounded"
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                      )}

                      {space.tags && space.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {space.tags.map((tag) => (
                            <span
                              key={tag.text}
                              className="text-xs bg-purple-500/20 px-2 py-1 rounded"
                            >
                              {tag.text}
                            </span>
                          ))}
                        </div>
                      )}

                      {space.features && space.features.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {space.features.map((feature) => (
                            <span
                              key={feature.text}
                              className="text-xs bg-green-500/20 px-2 py-1 rounded"
                            >
                              {feature.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
